import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
    get as mobxGet,
    set as mobxSet
} from 'mobx';
import {
    canConfigAiLab,
    canUseAiLab,
    dayjs,
    getApi,
    includes,
    post,
    setTrackerConfig
} from '~/utils';
import { ICorpThemeConfig, SuiteId, VipAddOnKey } from '~/interfaces';
import { API, FUNCTION_DISABLED } from '~/constants';
import {
    AuthModel,
    CorpType,
    PlatformVipAddOnKey,
    Region,
    Timezone,
    WatermarkConfig
} from '@fx-ui/platform-sdk';

interface IModules {
    // AI实验室是否参与灰度测试
    ai_lab_beta?: boolean;
    // AI实验室功能开启状态
    ai_lab?: boolean;
    /**
     * AI搜索入口是否参与灰度测试
     */
    ai_search_entry_beta?: boolean;
    // 系统地区
    region?: boolean;
    kms?: boolean; // 是否启用了知识管理
    /** 数据源可用 */
    data_source?: boolean;
    // MCP服务（个人api）开启状态
    member_api_key?: boolean;
    // MCP 协议
    member_api_key_license?: boolean;
}

interface TenantAiCapability {
    ai_lab?: {
        auto_fill: boolean;
        knowledge_qa: boolean;
        speech_recognition: boolean;
        rich_text_assist: boolean;
        field_auto_fill: boolean;
        field_suggestion: boolean;
        aggregation_formula: boolean;
        automation_auto_match: boolean;
        llm_node: boolean;
        llm_multi_modal_node: boolean;
        automation_ai_agent: boolean;
        flow_ai_guidance: boolean;
        generate_modify_function: boolean;
    };
}

/**
 * 当前登录用户、企业、套餐等信息
 */
export default class CorpUserModel extends AuthModel<{
    member: {
        has_file_manage?: boolean;
        /** 最近一次访问的子产品 */
        latest_access_suite_id?: SuiteId;
    };
    tenant: {
        /** 是否为企微多模板企业  */
        is_wxwork_suite_corp?: boolean;
        modules: IModules;
        // 版本包含知识库功能
        has_kms: boolean;
        ai_capability: TenantAiCapability;
    };
}> {
    private legacyUserInfo: Record<string, any>;

    constructor() {
        super();
        /**
         * private/protected字段会导致makeObservable有类型报错，所以需要利用它本身的泛型参数特殊处理一下
         * 注意，加入泛型参数列表中的属性会失去原本的类型推断，所以如果要 refactor 时要单独检查是否有遗漏
         * @see https://www.mobxjs.com/observable-state#%E5%B1%80%E9%99%90%E6%80%A7 第八点
         */
        makeObservable<
            CorpUserModel,
            'tenant' | 'platformVip' | 'member' | 'user' | '_authSuiteId'
        >(this, {
            user: observable,
            member: observable,
            tenant: observable,
            platformVip: observable.ref,
            _authSuiteId: observable,
            authSuiteId: computed,
            init: action,
            initConfig: action,
            region: computed,
            timezone: computed,
            isUserExist: computed,
            isMemberExist: computed,
            isCurrentCorpExist: computed,
            isCurrentUserNoCorp: computed,
            userLoginType: computed,
            isUserLoginFromSSO: computed,
            userId: computed,
            username: computed,
            userNickname: computed,
            userLocale: computed,
            userPhone: computed,
            userEmail: computed,
            userPhoneVerified: computed,
            userEmailVerified: computed,
            userHasPassword: computed,
            memberId: computed,
            memberUsername: computed,
            memberNickname: computed,
            memberType: computed,
            isExternalMember: computed,
            isSyncMember: computed,
            hasSysAdmin: computed,
            isCorpOwner: computed,
            hasPlatformManager: computed,
            hasOpenPlatformManager: computed,
            hasContactPermission: computed,
            hasTeamReadPermission: computed,
            hasTeamWritePermission: computed,
            hasRolePermission: computed,
            hasCorpCoopManagePermission: computed,
            hasContactInternalPermission: computed,
            hasExternalMemberManagePermission: computed,
            hasExternalRoleManagePermission: computed,
            hasContactExternalPermission: computed,
            isExternalUserAvailable: computed,
            memberAvatar: computed,
            isIntegratedCorpType: computed,
            userOwnedTenantList: computed,
            isOwnedTenant: action.bound,
            isCurrentUserNoTenant: computed,
            currentCorpName: computed,
            corpOwnerId: computed,
            currentCorpType: computed,
            hasISVEncryptedIntegration: computed,
            isISVEncrypted: computed,
            isDingTalkCorp: computed,
            tenantId: computed,
            corpThemeEnabled: computed,
            corpThemeColor: computed,
            userRegisteredDays: computed,
            currentCorpId: computed,
            isDingTalkCP: computed,
            hasResignedPermission: computed,
            watermarkConfig: computed,
            isWxworkSuiteCorp: computed,
            tmpTenantName: computed,
            updateWatermark: action.bound,
            setCorpTimezone: action.bound,
            setRegion: action.bound,
            setCorpTheme: action.bound,
            isRegionEnabled: computed,
            isAILabBetaEnabled: computed,
            isAILabEnabled: computed,
            isDataSourceEnabled: computed,
            setModulesKeyAndVal: action.bound,
            setAILabEnable: action.bound,
            setAILabDisable: action.bound,
            hasKMSFunc: computed,
            isKMSEnabled: computed,
            canConfigAiLab: computed,
            canUseAiLab: computed,
            latestAccessSuiteId: computed,
            isMcpEnabled: computed,
            isMcpAgreementAccepted: computed
        });
    }

    /**
     * 初始化用户登录信息
     */
    init() {
        return Promise.all([this.fetchLegacyLoginUser(), super.init({ enableHowxm: true })]).then(
            ([legacyInfo]) => {
                this.legacyUserInfo = legacyInfo;
                setTrackerConfig({
                    user_id: this.userId,
                    member_id: this.memberId,
                    username: this.username,
                    // todo 待删 by liaw
                    corp_id: this.tenant?.corp_id ?? '',
                    tenant_id: this.tenantId,
                    is_owner: this.isCorpOwner,
                    is_admin: this.hasSysAdmin,
                    is_platform_manager: this.hasPlatformManager,
                    is_external_user: this.isExternalMember,
                    vip_level: this.platformVipLevel
                });
            }
        );
    }

    fetchLegacyLoginUser() {
        /**
         * corp 下页面 jdy_router_switch 始终为 true
         * 通过 fx-code 访问的页面(eg.开放平台) jdy_router_switch = false
         */
        if (window.jdy_router_switch) {
            return Promise.resolve({});
        }
        return post(
            API.get_login_user,
            { suite_id: SuiteId.APP_BUILDER },
            {
                customHandleHttpCodes: [401]
            }
        )
            .then((res) => res.data)
            .catch(() => ({}));
    }

    /**
     * 获取Intercom身份验证的user_hash参数
     */
    get customerServiceUserHash() {
        return this.user?.customer_service_user_hash ?? '';
    }

    /**
     * 用户是否通过SSO登录
     */
    get isUserLoginFromSSO() {
        return this.userLoginType === 'sso';
    }

    /**
     * JDY 功能是否允许增购
     * @param key VipAddOnKey
     * @deprecated
     */
    allowAddonInAppBuilder(key: VipAddOnKey) {
        return includes(this.legacyUserInfo?.vip?.upgradable_addons, key);
    }

    /**
     * 平台套餐是否包含指定功能
     * @param key PlatformVipAddOnKey
     */
    hasPlatformVipFunction(key: PlatformVipAddOnKey) {
        return this.platformVip?.pack[key] !== FUNCTION_DISABLED;
    }

    /**
     * 平台套餐指定功能用量上限
     * @param key PlatformVipAddOnKey
     */
    getPlatformVipFunctionLimit(key: PlatformVipAddOnKey) {
        return this.platformVip?.pack[key] || 0;
    }

    /**
     * 应用搭建套餐是否包含指定功能
     * @param key VipAddOnKey
     * @deprecated
     */
    hasAppBuilderVipFunction(key: VipAddOnKey) {
        return (
            this.legacyUserInfo?.vip && this.legacyUserInfo?.vip?.pack[key] !== FUNCTION_DISABLED
        );
    }

    /**
     * 注册天数
     */
    get userRegisteredDays() {
        const regTime = this.userRegTime;
        return regTime && dayjs().diff(dayjs(regTime), 'day');
    }

    /**
     * 获取当前企业id
     * TODO @liaw 需要排查一下使用场景，按需替换为tenantId
     * @deprecated
     */
    get currentCorpId() {
        return this.tenant?.corp_id ?? '';
    }

    /**
     * 是否是钉钉cp模式
     */
    get isDingTalkCP() {
        return this.currentCorpType === CorpType.DingTalk && this.tenant?.is_corp_secret;
    }

    /**
     * 是否为企微多模板企业
     */
    get isWxworkSuiteCorp() {
        return this.tenant?.is_wxwork_suite_corp ?? false;
    }

    /**
     * 是否有离职人员管理权限
     */
    get hasResignedPermission() {
        return this.member?.has_dismission_manage_permission;
    }

    /**
     * 设置企业时区
     * @param timezone 时区
     */
    setCorpTimezone(timezone: Timezone) {
        if (this.tenant) {
            this.tenant = { ...this.tenant, timezone };
        }
    }

    /**
     * 设置系统地区
     * @param region
     */
    setRegion(region: Region) {
        if (this.tenant) {
            this.tenant = { ...this.tenant, region };
        }
    }

    /**
     * 更新水印
     * @param watermark 水印配置
     */
    updateWatermark = (watermark: WatermarkConfig) => {
        if (this.tenant) {
            // 对于新账号，corp中可能不存在watermark，需要用set来手动使其observable
            mobxSet(this.tenant, 'watermark', watermark);
        }
    };

    /** 获取水印配置 */
    get watermarkConfig(): WatermarkConfig | undefined {
        if (!this.tenant) {
            return undefined;
        }
        return mobxGet(this.tenant, 'watermark');
    }

    /**
     * 设置企业主题配置（页面不刷新）
     *
     * @param themeConfig 企业主题配置
     */
    setCorpTheme(themeConfig: ICorpThemeConfig) {
        if (this.tenant) {
            this.tenant = { ...this.tenant, corp_theme: themeConfig };
        }
    }

    /**
     * 获取系统地区是否参与灰度测试状态
     */
    get isRegionEnabled() {
        return this.tenant?.modules.region ?? false;
    }

    /**
     * 获取知识管理开启状态
     */
    get isKMSEnabled() {
        return this.tenant?.modules.kms ?? false;
    }

    /**
     * 版本包含知识库功能
     */
    get hasKMSFunc() {
        return this.tenant?.has_kms ?? false;
    }

    /**
     * 获取AI实验室是否参与灰度测试状态
     */
    get isAILabBetaEnabled() {
        return this.tenant?.modules.ai_lab_beta ?? false;
    }

    /**
     * 获取AI搜索入口参与灰度测试状态
     */
    get isAISearchEntryBetaEnabled() {
        return this.tenant?.modules.ai_search_entry_beta ?? false;
    }

    /**
     * 获取AI实验室功能开启状态
     */
    get isAILabEnabled() {
        return this.tenant?.modules.ai_lab ?? false;
    }

    /**
     * 版本包含AI实验室（jodoo不包含）
     * 套餐包含AI实验室/AI实验室内测用户
     */
    get canConfigAiLab() {
        return canConfigAiLab(this);
    }

    /**
     * 版本包含AI实验室（jodoo不包含）
     * 套餐包含AI实验室/AI实验室内测用户
     * 开启了AI实验室
     */
    get canUseAiLab() {
        return canUseAiLab(this);
    }

    /** 数据源可用状态 */
    get isDataSourceEnabled() {
        return this.tenant?.modules?.data_source ?? false;
    }

    /**
     * 是否有附件存储管理入口
     */
    get hasStorageMgmtEntry() {
        return this.member?.has_file_manage ?? false;
    }

    /** MCP服务（个人api）开启状态 */
    get isMcpEnabled() {
        return this.tenant?.modules?.member_api_key ?? false;
    }

    /** MCP 协议接受状态 */
    get isMcpAgreementAccepted() {
        return this.tenant?.modules?.member_api_key_license ?? false;
    }

    /**
     * 设置模块状态
     * @param key 模块键
     * @param value 模块值
     */
    setModulesKeyAndVal(key: keyof IModules, value: boolean) {
        const modules = { ...this.tenant!.modules, [key]: value };
        this.tenant = { ...this.tenant!, modules: modules };
    }

    /**
     * 开启AI实验室
     */
    setAILabEnable() {
        return post<TenantAiCapability>(getApi(API.ai.update_lab_conf, this.tenantId), {
            enable: true
        }).then((res) => {
            this.setModulesKeyAndVal('ai_lab', true);
            runInAction(() => {
                this.tenant = {
                    ...this.tenant!,
                    ai_capability: res.data
                };
            });
        });
    }

    /**
     * 关闭AI实验室
     */
    setAILabDisable() {
        return post(getApi(API.ai.update_lab_conf, this.tenantId), {
            enable: false
        }).then(() => {
            this.setModulesKeyAndVal('ai_lab', false);
        });
    }

    /**
     * 检查AI功能启用情况
     * @param capability 功能key
     * @returns 是否开启
     */
    checkAiLabCapability(capability: keyof NonNullable<TenantAiCapability['ai_lab']>) {
        return this.tenant?.ai_capability?.ai_lab?.[capability] ?? false;
    }

    /**
     * 最近一次访问的子产品
     */
    get latestAccessSuiteId() {
        return this.member?.latest_access_suite_id;
    }
}
